const STORAGE_KEY = 'pindooCheckoutBeadsOnlyAcknowledged';
const GUARD_SELECTOR = '[data-pindoo-checkout-guard]';
const COUNTRY_STORAGE_KEY = 'pindooCartShippingCountry';
const COUNTRY_SOURCE_STORAGE_KEY = 'pindooCartShippingCountrySource';
const COUNTRY_STATE_STORAGE_KEY = 'pindooCartShippingCountryV2';
const AUTO_COUNTRY_MAX_AGE = 24 * 60 * 60 * 1000;
const SHIPPING_RATES_URL = window.PindooShippingRatesUrl || '/cdn/shop/t/3/assets/pindoo-shipping-rates.json';

let pendingCheckoutButton = null;
let checkoutCountryNames = {
  NL: 'Netherlands',
};

const checkoutCountryNamesPromise = fetch(SHIPPING_RATES_URL, { cache: 'no-store' })
  .then((response) => response.json())
  .then((data) => {
    if (!Array.isArray(data?.countries)) return;

    checkoutCountryNames = data.countries.reduce(
      (countryNames, country) => {
        if (country.code && country.name) countryNames[country.code] = country.name;
        return countryNames;
      },
      { ...checkoutCountryNames }
    );
  })
  .catch(() => {
    // Checkout must keep working even if the local rates asset cannot be loaded.
  });

const escapeHtml = (value) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[character])
  );

const isSuppressed = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const suppressFuturePrompts = () => {
  try {
    window.localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Checkout should never be blocked by storage availability.
  }
};

const getStoredCheckoutCountryName = () => {
  try {
    if (typeof window.PindooGetCartShippingCountry === 'function') {
      return window.PindooGetCartShippingCountry()?.name || null;
    }

    const saved = window.localStorage.getItem(COUNTRY_STATE_STORAGE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      const age = Date.now() - state?.updatedAt;
      const valid = state?.source === 'manual' ||
        (state?.source === 'auto' && Number.isFinite(state.updatedAt) && age >= 0 && age < AUTO_COUNTRY_MAX_AGE);
      return valid && Object.hasOwn(checkoutCountryNames, state?.code) ? checkoutCountryNames[state.code] : null;
    }

    const countryCode = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (window.localStorage.getItem(COUNTRY_SOURCE_STORAGE_KEY) === 'manual' && Object.hasOwn(checkoutCountryNames, countryCode)) {
      return checkoutCountryNames[countryCode];
    }
  } catch {
    // Missing or invalid country state must never force a checkout destination.
  }

  return null;
};

const getCheckoutUrl = () => {
  const url = new URL('/checkout', window.location.origin);
  const countryName = getStoredCheckoutCountryName();
  if (countryName) url.searchParams.set('checkout[shipping_address][country]', countryName);
  return `${url.pathname}${url.search}`;
};

const redirectToCheckout = () => {
  const waitForCountryNames = Promise.race([
    checkoutCountryNamesPromise,
    new Promise((resolve) => {
      window.setTimeout(resolve, 500);
    }),
  ]);

  waitForCountryNames.finally(() => {
    window.location.href = getCheckoutUrl();
  });
};

const shouldWarn = (button) =>
  button instanceof HTMLElement &&
  button.dataset.pindooCheckoutWarn === 'true' &&
  button.dataset.pindooCheckoutBypass !== 'true' &&
  !isSuppressed();

const getOrCreateDialog = () => {
  let dialog = document.querySelector('.pindoo-checkout-guard');

  if (dialog instanceof HTMLDialogElement) return dialog;

  dialog = document.createElement('dialog');
  dialog.className = 'pindoo-checkout-guard';
  const dismissLabel = escapeHtml(pendingCheckoutButton?.dataset.pindooDismissLabel || "Don't remind me again");

  dialog.innerHTML = `
    <p class="pindoo-checkout-guard__title">
      to make DIY beads puzzle, users need at least:
    </p>
    <ul class="pindoo-checkout-guard__list">
      <li class="pindoo-checkout-guard__item">
        <input type="checkbox" data-pindoo-guard-beads disabled>
        <a href="/collections/beads-set">pindoo beads</a>
      </li>
      <li class="pindoo-checkout-guard__item">
        <input type="checkbox" data-pindoo-guard-tool-set disabled>
        <span>
          <a href="/products/tools?variant=53037902070088">Starter Set</a>
          or
          <a href="/products/tools?variant=53037902102856">Creator Set</a>
        </span>
      </li>
      <li class="pindoo-checkout-guard__item">
        <input type="checkbox" data-pindoo-guard-pegboard disabled>
        <a href="/products/tools?variant=53043483935048">Pegboard</a>
      </li>
    </ul>
    <div class="pindoo-checkout-guard__footer">
      <label class="pindoo-checkout-guard__dismiss">
        <input type="checkbox" data-pindoo-guard-dismiss>
        <span data-pindoo-guard-dismiss-label>${dismissLabel}</span>
      </label>
      <button type="button" class="pindoo-checkout-guard__proceed" data-pindoo-guard-proceed>
        Proceed >>>
      </button>
    </div>
  `;

  document.body.append(dialog);

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  dialog.querySelector('[data-pindoo-guard-proceed]')?.addEventListener('click', () => {
    const dismiss = dialog.querySelector('[data-pindoo-guard-dismiss]');

    if (dismiss instanceof HTMLInputElement && dismiss.checked) {
      suppressFuturePrompts();
    }

    dialog.close();
    proceedToCheckout();
  });

  return dialog;
};

const updateDialogState = (dialog, button) => {
  const beads = dialog.querySelector('[data-pindoo-guard-beads]');
  const toolSet = dialog.querySelector('[data-pindoo-guard-tool-set]');
  const pegboard = dialog.querySelector('[data-pindoo-guard-pegboard]');
  const dismissLabel = dialog.querySelector('[data-pindoo-guard-dismiss-label]');

  if (beads instanceof HTMLInputElement) {
    beads.checked = button.dataset.pindooHasBeads === 'true';
  }

  if (toolSet instanceof HTMLInputElement) {
    toolSet.checked = button.dataset.pindooHasToolSet === 'true';
  }

  if (pegboard instanceof HTMLInputElement) {
    pegboard.checked = button.dataset.pindooHasPegboard === 'true';
  }

  if (dismissLabel instanceof HTMLElement) {
    dismissLabel.textContent = button.dataset.pindooDismissLabel || "Don't remind me again";
  }
};

const showGuard = (button) => {
  pendingCheckoutButton = button;

  const dialog = getOrCreateDialog();
  updateDialogState(dialog, button);

  if (!dialog.open) {
    dialog.showModal();
  }
};

const proceedToCheckout = () => {
  pendingCheckoutButton = null;
  redirectToCheckout();
};

document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest(GUARD_SELECTOR) : null;

    if (!shouldWarn(button)) return;

    event.preventDefault();
    event.stopPropagation();
    showGuard(button);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const submitter = event.submitter;
    const button = submitter instanceof Element ? submitter.closest(GUARD_SELECTOR) : null;

    if (!shouldWarn(button)) return;

    event.preventDefault();
    event.stopPropagation();
    showGuard(button);
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest(GUARD_SELECTOR) : null;

    if (!(button instanceof HTMLElement) || shouldWarn(button)) return;

    event.preventDefault();
    event.stopPropagation();
    redirectToCheckout();
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const submitter = event.submitter;
    const button = submitter instanceof Element ? submitter.closest(GUARD_SELECTOR) : null;

    if (!(button instanceof HTMLElement) || shouldWarn(button)) return;

    event.preventDefault();
    event.stopPropagation();
    redirectToCheckout();
  },
  true
);
